import type { CarriedItem, Equipment } from './types/game';
import type {
    EmpyrealCrystalItem,
    EmpyrealCrystalKind,
    EmpyrealCrystalSize,
    EmpyrealCrystalType,
    EmpyrealCrystalStack,
} from './types/crystals';
import type { RecipeDefinition } from './types/essence';
import { EMPYREAL_CRYSTAL_STACK_LIMITS } from './types/crystals';

export function getEmpyrealCrystalStackLimit(size: EmpyrealCrystalSize): number {
    return EMPYREAL_CRYSTAL_STACK_LIMITS[size];
}

export function isEmpyrealCrystalType(candidate: unknown): candidate is EmpyrealCrystalType {
    return candidate === 'resonance' || candidate === 'vitality' || candidate === 'insight' || candidate === 'warding' || candidate === 'fury';
}

/**
 * A carried inventory as a slot-indexed array: `undefined` entries are empty
 * slots. Mirrors how `PlayerCharacter.inventory` is stored at runtime.
 */
export type CarriedInventorySlots = ReadonlyArray<CarriedItem | undefined>;

export function isEmpyrealCrystalItem(item: CarriedItem | null | undefined): item is EmpyrealCrystalItem {
    return item !== null && item !== undefined && (item as Partial<EmpyrealCrystalItem>).kind === 'empyreal-crystal';
}

/** True for a real equipment item (not a crystal stack, not an empty slot). */
export function isCarriedEquipment(item: CarriedItem | null | undefined): item is Equipment {
    return item !== null && item !== undefined && !isEmpyrealCrystalItem(item);
}

/** The kind of crystal a recipe coalesces. */
export function toCrystalKind(recipe: Pick<RecipeDefinition, 'id' | 'crystalType' | 'size'>): EmpyrealCrystalKind {
    return { recipeId: recipe.id, crystalType: recipe.crystalType, size: recipe.size };
}

/** Whether two crystals stack together: they came from the same recipe. */
export function isSameCrystalKind(item: EmpyrealCrystalKind, kind: EmpyrealCrystalKind): boolean {
    return item.recipeId === kind.recipeId && item.crystalType === kind.crystalType && item.size === kind.size;
}

/**
 * How many more crystals of one kind the carried inventory can hold: the
 * free space in existing same-kind stacks plus a full stack per empty slot.
 */
export function resolveCarriedCrystalRoom(
    inventory: CarriedInventorySlots,
    totalSlots: number,
    kind: EmpyrealCrystalKind,
): number {
    const limit = getEmpyrealCrystalStackLimit(kind.size);
    let room = 0;
    for (let index = 0; index < totalSlots; index += 1) {
        const item = inventory[index];
        if (item === undefined || item === null) {
            room += limit;
        } else if (isEmpyrealCrystalItem(item) && isSameCrystalKind(item, kind)) {
            room += Math.max(0, limit - item.quantity);
        }
    }
    return room;
}

/** Result of adding crystals to a carried inventory. */
export interface CarriedCrystalAddResult {
    readonly inventory: Array<CarriedItem | undefined>;
    /** How many of the requested crystals fit. */
    readonly added: number;
}

/**
 * Adds crystals to a carried inventory, topping up existing same-kind stacks
 * first, then filling empty slots. Returns a new array; crystals that don't
 * fit are not added. `createId` names each new stack.
 */
export function withCarriedCrystalsAdded(
    inventory: CarriedInventorySlots,
    totalSlots: number,
    stack: EmpyrealCrystalStack,
    createId: () => string,
): CarriedCrystalAddResult {
    const limit = getEmpyrealCrystalStackLimit(stack.size);
    const next: Array<CarriedItem | undefined> = Array.from({ length: Math.max(totalSlots, inventory.length) }, (_, index) => inventory[index]);
    let remaining = Math.max(0, Math.floor(stack.quantity));

    for (let index = 0; index < totalSlots && remaining > 0; index += 1) {
        const item = next[index];
        if (isEmpyrealCrystalItem(item) && isSameCrystalKind(item, stack) && item.quantity < limit) {
            const moved = Math.min(remaining, limit - item.quantity);
            next[index] = { ...item, quantity: item.quantity + moved };
            remaining -= moved;
        }
    }

    for (let index = 0; index < totalSlots && remaining > 0; index += 1) {
        if (next[index] === undefined || next[index] === null) {
            const moved = Math.min(remaining, limit);
            next[index] = {
                id: createId(),
                kind: 'empyreal-crystal',
                recipeId: stack.recipeId,
                crystalType: stack.crystalType,
                size: stack.size,
                quantity: moved,
            };
            remaining -= moved;
        }
    }

    return { inventory: next, added: Math.max(0, Math.floor(stack.quantity)) - remaining };
}

/**
 * Removes one crystal from the stack with `itemId`, emptying its slot when
 * the stack runs out. Returns undefined when there is no such crystal stack.
 */
export function withOneCarriedCrystalTaken(
    inventory: CarriedInventorySlots,
    itemId: string,
): { inventory: Array<CarriedItem | undefined>; crystal: EmpyrealCrystalItem } | undefined {
    const index = inventory.findIndex((item) => isEmpyrealCrystalItem(item) && item.id === itemId);
    const item = index >= 0 ? inventory[index] : undefined;
    if (!isEmpyrealCrystalItem(item) || item.quantity <= 0) {
        return undefined;
    }

    const next = [...inventory];
    next[index] = item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : undefined;
    return { inventory: next, crystal: item };
}

/** Total crystals of one kind across all carried stacks. */
export function countCarriedCrystals(
    inventory: CarriedInventorySlots,
    kind: EmpyrealCrystalKind,
): number {
    return inventory.reduce((total, item) => (
        isEmpyrealCrystalItem(item) && isSameCrystalKind(item, kind) ? total + item.quantity : total
    ), 0);
}

/**
 * The carried inventory with every crystal stack removed (its slot left
 * empty). Crystals are run-bound, so this is what gets persisted.
 */
export function withoutCarriedCrystals(inventory: CarriedInventorySlots): Array<Equipment | undefined> {
    return inventory.map((item) => (isCarriedEquipment(item) ? item : undefined));
}

/**
 * Whether one crystal of this kind can go into a specific carried slot: the
 * slot exists and is empty, or holds a same-kind stack with room.
 */
export function canAddCrystalToCarriedSlot(
    inventory: CarriedInventorySlots,
    totalSlots: number,
    slotIndex: number,
    kind: EmpyrealCrystalKind,
): boolean {
    if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= totalSlots) {
        return false;
    }
    const item = inventory[slotIndex];
    if (item === undefined || item === null) {
        return true;
    }
    return isEmpyrealCrystalItem(item)
        && isSameCrystalKind(item, kind)
        && item.quantity < getEmpyrealCrystalStackLimit(kind.size);
}

/**
 * Adds one crystal to a specific carried slot (new stack in an empty slot, or
 * onto a same-kind stack). Returns undefined when it can't go there.
 */
export function withCrystalAddedToSlot(
    inventory: CarriedInventorySlots,
    totalSlots: number,
    slotIndex: number,
    kind: EmpyrealCrystalKind,
    createId: () => string,
): Array<CarriedItem | undefined> | undefined {
    if (!canAddCrystalToCarriedSlot(inventory, totalSlots, slotIndex, kind)) {
        return undefined;
    }
    const next: Array<CarriedItem | undefined> = Array.from({ length: Math.max(totalSlots, inventory.length) }, (_, index) => inventory[index]);
    const item = next[slotIndex];
    next[slotIndex] = isEmpyrealCrystalItem(item)
        ? { ...item, quantity: item.quantity + 1 }
        : { id: createId(), kind: 'empyreal-crystal', ...kind, quantity: 1 };
    return next;
}
